import { createGraphics } from '../../rendering/utils/Graphics';
import type { IGraphics } from '../../rendering/IRenderer';
import { delay as delayMs } from '../../../shared/utils/Timer';
import { SCREEN_EFFECTS } from '../../../shared/config/screen-effects.config';
import { getViewportSize } from './viewport';
import { getMotionPrefs } from '../../systems/accessibility/motionPrefs';

/** Minimum gap between full-screen flashes — anti-strobe floor so multi-hit
 *  combat bursts can't machine-gun the viewport (photosensitivity safety,
 *  applies to everyone regardless of prefs). */
const MIN_FLASH_INTERVAL_MS = 120;

/**
 * ScreenEffectManager — camera-wide visual effects (flash + tint).
 *
 * Phase H9 Batch 2 (2026-05-08): screenShake removed. The previous impl
 * mutated camera.scrollX/scrollY directly via update() — but update()
 * was never wired into any system's tick loop, so shake state was set
 * and never decayed. The 16+ ability files emitting `vfx:screen_shake`
 * went into a dead path. Routing to CameraManager.shake (which IS
 * updated each frame via CameraSystem) fixes the bug AND removes the
 * Phaser-camera coupling that this file used to carry.
 */
export class ScreenEffectManager {
  private scene: unknown;

  // State (tint-only; shake moved to CameraManager.shake)
  private screenTintColor: number | null = null;
  private screenTintDuration: number = 0;
  private screenTintStartTime: number = 0;
  // Last flash timestamp — anti-strobe throttle floor (see screenFlash).
  // -Infinity so the very first flash is never throttled (even at low t).
  private lastFlashAt: number = Number.NEGATIVE_INFINITY;
  /**
   * One persistent flash rect (plan 16 §2.2), shown by alpha and left at
   * alpha 0 between flashes. Creating and destroying one per flash added and
   * removed a child — two Pixi stage rebuilds per flash.
   */
  private flash: IGraphics | null = null;
  /** Bumped per flash so an older fade loop stops when a new one starts. */
  private flashSeq = 0;

  constructor(scene: unknown) {
    this.scene = scene;
  }

  /**
   * Set the scene reference (e.g. on scene restart)
   */
  public setScene(scene: unknown): void {
    this.scene = scene;
  }

  /**
   * Apply screen flash effect. Substrate-aware via Stage 5r screen-space
   * IGraphics: full-screen tinted rect on the UI layer, rAF-driven fade
   * to alpha 0 over `duration` ms, then parked at alpha 0 for the next flash. The flash
   * stays fixed regardless of camera scroll because it's screen-space.
   *
   * VFX & Rendering Parity Lane 2 (2026-06-03): the flash extent is sourced
   * from the LIVE viewport (`getViewportSize`, backed by the Pixi overlay's
   * `resizeTo: window` screen), not the camera's "intended frame" — which
   * defaults to 1280×720 and is the letterboxed *world* frame, not the
   * canvas. The previous hardcoded `1280×720` fallback drew the flash as a
   * fixed rectangle that under-covered larger viewports. Reading per-call
   * means resizes are picked up the next time a flash fires; the flash is
   * recomputed at full viewport extent each time. Color/alpha/duration
   * tunables live in `screen-effects.config.ts`.
   */
  public screenFlash(
    color: number = SCREEN_EFFECTS.flashColor,
    duration: number = SCREEN_EFFECTS.flashDurationMs,
  ): void {
    // Accessibility (photosensitivity): suppress full-screen flashes entirely
    // when the player opted out, and throttle rapid-fire bursts into a
    // non-strobing cadence for everyone. The open public QA/stream tunnel makes
    // this a real concern — a visitor mid-brawl gets a flash ~per hit.
    if (getMotionPrefs().disableScreenFlash) return;
    const now = performance.now();
    if (now - this.lastFlashAt < MIN_FLASH_INTERVAL_MS) return;
    this.lastFlashAt = now;

    const { width, height } = getViewportSize();
    const flash = this.flash ?? (this.flash = createGraphics(this.scene, { depth: 1000, screenSpace: true }));
    // Rewind + redraw: an unchanged colour/viewport is deduped by PixiGraphics
    // and touches nothing; a new colour or size redraws this one rect.
    flash.clear();
    flash.fillRect(0, 0, width, height, color, SCREEN_EFFECTS.flashAlpha);
    flash.setAlpha(1);

    const seq = ++this.flashSeq;
    const startTime = performance.now();
    const tick = () => {
      if (seq !== this.flashSeq || this.flash !== flash) return;
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      flash.setAlpha(1 - t);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /**
   * Apply screen tint effect
   */
  public screenTint(color: number, duration: number): void {
    this.screenTintColor = color;
    this.screenTintDuration = duration;
    this.screenTintStartTime = performance.now();

    // Schedule removal of effect (logical state only)
    const startedAt = this.screenTintStartTime;
    delayMs(duration, () => {
      // Only clear if our specific tint is still the active one (caller
      // hasn't started a new tint in the meantime).
      if (this.screenTintStartTime === startedAt) {
        this.screenTintColor = null;
      }
    });
  }

  /**
   * Update screen effects
   */
  public update(_time: number, delta: number): void {
    this.updateScreenTint(delta);
  }

  private updateScreenTint(_delta: number): void {
    if (this.screenTintDuration > 0 && this.screenTintColor !== null) {
      const elapsed = performance.now() - this.screenTintStartTime;
      if (elapsed >= this.screenTintDuration) {
        this.screenTintColor = null;
        this.screenTintDuration = 0;
      }
    }
  }

  /**
   * Cleanup all screen effects
   */
  public cleanup(): void {
    this.screenTintColor = null;
    this.screenTintDuration = 0;
    this.flash?.destroy();
    this.flash = null;
  }
}
